import { useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Clock,
  Lock,
  PanelRightClose,
  PanelRightOpen,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/context";
import { ROUTES } from "@/lib/constants";
import { formatCostIDR, formatCostUSD } from "@/lib/format";
import { cn } from "@/lib/utils";
import { STEP_ORDER } from "../use-playground";
import type {
  GovernanceStatus,
  GovernanceStep,
  GovernanceStepId,
  PlaygroundMessage,
} from "../types";

type Translate = ReturnType<typeof useTranslation>["t"];

function stepLabel(t: Translate, id: GovernanceStepId): string {
  switch (id) {
    case "policy":
      return t("playground.governance.stepLabels.policy");
    case "pii":
      return t("playground.governance.stepLabels.pii");
    case "routing":
      return t("playground.governance.stepLabels.routing");
    case "provider":
      return t("playground.governance.stepLabels.provider");
    case "audit":
      return t("playground.governance.stepLabels.audit");
  }
}

interface Props {
  open: boolean;
  onToggle(): void;
  message: PlaygroundMessage | null;
}

/**
 * Governance pipeline panel (372px expanded · 44px collapsed).
 *
 * Renders the five-step vertical stepper for the currently bound
 * assistant message. The summary footer carries latency / tokens /
 * audit deep-link.
 */
export function PlaygroundGovernancePanel({ open, onToggle, message }: Props) {
  if (!open) return <CollapsedRail onToggle={onToggle} message={message} />;
  return <ExpandedPanel onToggle={onToggle} message={message} />;
}

function CollapsedRail({
  onToggle,
  message,
}: {
  onToggle(): void;
  message: PlaygroundMessage | null;
}) {
  const { t } = useTranslation();
  const flagged = countFlagged(message);
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={t("playground.governance.expandAriaLabel")}
      aria-expanded={false}
      className="flex h-full w-full flex-col items-center gap-2 border-l py-3 transition-colors hover:bg-[var(--pg-blue-soft)]/40"
      style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
    >
      <PanelRightOpen size={16} style={{ color: "var(--pg-text-3)" }} />
      <ShieldCheck size={16} style={{ color: "var(--pg-blue)" }} />
      <div className="mt-1 flex flex-col items-center gap-1.5">
        {(message?.governance?.steps ?? []).map((s) => (
          <span
            key={s.id}
            className={cn("h-2 w-2 rounded-full", dotForStatus(s.status))}
            title={`${stepLabel(t, s.id)} · ${s.status}`}
          />
        ))}
      </div>
      <div
        className="mono mt-auto rotate-180 text-xs [writing-mode:vertical-rl]"
        style={{ color: "var(--pg-text-3)" }}
      >
        {flagged > 0
          ? t("playground.governance.railLabelFlagged", { n: flagged })
          : t("playground.governance.railLabel")}
      </div>
    </button>
  );
}

function ExpandedPanel({
  onToggle,
  message,
}: {
  onToggle(): void;
  message: PlaygroundMessage | null;
}) {
  const { t } = useTranslation();
  const steps = message?.governance?.steps ?? defaultSteps();
  const flagged = countFlagged(message);
  return (
    <aside
      className="flex h-full w-full flex-col border-l"
      style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
    >
      {/* Header */}
      <header className="border-b px-4 pb-3 pt-4" style={{ borderColor: "var(--pg-border-soft)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} style={{ color: "var(--pg-blue)" }} />
            <span
              className="text-sm font-medium tracking-tight"
              style={{ color: "var(--pg-text)" }}
            >
              {t("playground.governance.panelTitle")}
            </span>
            {message?.governance?.reqId && (
              <span
                className="mono ml-1 rounded px-1.5 py-[1px] text-xs"
                style={{
                  background: "var(--pg-bg)",
                  border: "1px solid var(--pg-border)",
                  color: "var(--pg-text-3)",
                }}
                title={message.governance.reqId}
              >
                req:{message.governance.reqId.slice(0, 8)}…
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onToggle}
            aria-label={t("playground.governance.collapseAriaLabel")}
            aria-expanded={true}
            className="text-[var(--pg-text-3)] hover:text-[var(--pg-text-2)]"
          >
            <PanelRightClose size={16} />
          </button>
        </div>
        <div
          className="mt-2 flex items-center gap-2 text-xs tabular-nums"
          style={{ color: "var(--pg-text-3)" }}
        >
          {message?.timing ? (
            <>
              <Clock size={11} />
              <span>{t("playground.governance.totalMs", { ms: message.timing.totalMs })}</span>
            </>
          ) : (
            <span>{t("playground.governance.awaitingTrace")}</span>
          )}
          {flagged > 0 && (
            <span className="pill pill--warn ml-auto">
              <AlertTriangle size={11} />
              {t("playground.governance.flaggedCount", { n: flagged })}
            </span>
          )}
        </div>
      </header>

      {/* Steps */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <ol className="space-y-1.5">
          {STEP_ORDER.map((id, i) => {
            const step =
              steps.find((s) => s.id === id) ??
              ({ id, meta: "pending", status: "pending", ms: 0, detail: null } as GovernanceStep);
            return <StepRow key={id} index={i} step={step} />;
          })}
        </ol>
      </div>

      {/* Summary */}
      <Summary message={message} />
    </aside>
  );
}

function StepRow({ index, step }: { index: number; step: GovernanceStep }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const Icon = iconForStatus(step.status);
  const showDetail = step.status === "flagged" || step.status === "blocked";
  const label = stepLabel(t, step.id);
  const displayMeta = compactMeta(step);

  return (
    <li
      className="rounded-lg border px-3 py-2"
      style={{
        borderColor:
          step.status === "blocked"
            ? "var(--pg-danger-ring)"
            : step.status === "flagged"
              ? "var(--pg-warn-ring)"
              : "var(--pg-border)",
        background: "var(--pg-surface)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
        aria-expanded={open}
        disabled={!showDetail && !step.detail}
      >
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={cn("status-dot", `status-dot--${step.status}`)}
            aria-label={`${label} ${step.status}`}
          >
            <Icon size={11} />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span
                className="mono text-xs"
                style={{ color: "var(--pg-text-3)" }}
              >
                {(index + 1).toString().padStart(2, "0")}
              </span>
              <span
                className="text-xs font-medium leading-tight"
                style={{ color: "var(--pg-text)" }}
              >
                {label}
              </span>
              <StatusPill status={step.status} />
            </div>
            <div
              className="mono mt-0.5 truncate text-xs"
              style={{ color: "var(--pg-text-3)" }}
              title={step.meta}
            >
              {displayMeta}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {step.ms > 0 && (
            <span
              className="mono text-xs"
              style={{ color: "var(--pg-text-3)" }}
            >
              {step.ms}ms
            </span>
          )}
          {(showDetail || step.detail) && (
            <ChevronRight
              size={13}
              className={cn("transition-transform", open && "rotate-90")}
              style={{ color: "var(--pg-text-3)" }}
            />
          )}
        </div>
      </button>
      {open && step.detail && (
        <div
          className="mono mt-2 rounded-md px-2.5 py-2 text-xs leading-relaxed"
          style={{
            background: "var(--pg-bg)",
            color: "var(--pg-text-2)",
            border: "1px solid var(--pg-border)",
          }}
        >
          <ProviderDetailExtras step={step} />
          {Object.entries(step.detail)
            .filter(([k]) => !shouldHideDetailKey(step, k))
            .map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <span style={{ color: "var(--pg-text-3)" }}>{k}:</span>
                <span className="break-all">
                  {typeof v === "string" || typeof v === "number"
                    ? String(v)
                    : Array.isArray(v)
                      ? v.join(", ")
                      : JSON.stringify(v)}
                </span>
              </div>
            ))}
        </div>
      )}
    </li>
  );
}

function Summary({ message }: { message: PlaygroundMessage | null }) {
  const { t } = useTranslation();
  const auditId = message?.governance?.auditId ?? null;
  const totalMs = message?.timing?.totalMs;
  const chunks = message?.usage?.chunks;
  const tokens = message?.usage?.tokens;
  const costUsd = message?.usage?.costUsd;
  const costIdr = message?.usage?.costIdr;

  return (
    <footer
      className="border-t px-4 py-3"
      style={{ borderColor: "var(--pg-border-soft)" }}
    >
      <dl className="space-y-1.5 text-xs tabular-nums">
        <SummaryRow label={t("playground.governance.summary.latency")}>
          {totalMs !== undefined ? `${totalMs} ms` : "—"}
        </SummaryRow>
        <SummaryRow label={t("playground.governance.summary.tokens")}>
          {tokens !== undefined
            ? `${tokens}`
            : chunks !== undefined
              ? `~${chunks} chunks`
              : "—"}
        </SummaryRow>
        <SummaryRow label={t("playground.governance.summary.cost")}>
          {costUsd !== undefined || costIdr !== undefined ? (
            <span>
              {costUsd !== undefined ? formatCostUSD(costUsd) : "—"}
              <span style={{ color: "var(--pg-text-3)" }}>{" · "}</span>
              {costIdr !== undefined ? formatCostIDR(costIdr) : "—"}
            </span>
          ) : (
            "—"
          )}
        </SummaryRow>
        <SummaryRow label={t("playground.governance.summary.audit")}>
          {auditId ? `${auditId.slice(0, 12)}…` : "—"}
        </SummaryRow>
      </dl>
      {auditId ? (
        <Button variant="subtle" size="sm" asChild className="mt-3 w-full">
          <Link to={`${ROUTES.OBSERVABILITY}?audit=${encodeURIComponent(auditId)}`}>
            {t("playground.governance.summary.viewAuditTrail")}
          </Link>
        </Button>
      ) : (
        <div
          className="mt-3 block rounded-md border px-3 py-2 text-center text-xs font-medium"
          style={{
            background: "var(--pg-bg)",
            border: "1px solid var(--pg-border)",
            color: "var(--pg-text-3)",
          }}
        >
          {t("playground.governance.summary.awaitingAuditId")}
        </div>
      )}
    </footer>
  );
}

function SummaryRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <dt
        className="eyebrow"
        style={{ color: "var(--pg-text-3)" }}
      >
        {label}
      </dt>
      <dd style={{ color: "var(--pg-text-2)" }}>{children}</dd>
    </div>
  );
}

function StatusPill({ status }: { status: GovernanceStatus }) {
  const { t } = useTranslation();
  switch (status) {
    case "done":
      return <span className="pill pill--ok">{t("playground.governance.statusPills.ok")}</span>;
    case "running":
      return <span className="pill pill--blue">{t("playground.governance.statusPills.running")}</span>;
    case "flagged":
      return <span className="pill pill--warn">{t("playground.governance.statusPills.flagged")}</span>;
    case "blocked":
      return <span className="pill pill--danger">{t("playground.governance.statusPills.blocked")}</span>;
    case "pending":
    default:
      return <span className="pill">{t("playground.governance.statusPills.pending")}</span>;
  }
}

function defaultSteps(): GovernanceStep[] {
  return STEP_ORDER.map((id) => ({
    id,
    meta: "pending",
    status: "pending" as GovernanceStatus,
    ms: 0,
    detail: null,
  }));
}

function iconForStatus(status: GovernanceStatus) {
  switch (status) {
    case "done":
      return Check;
    case "running":
      return Clock;
    case "flagged":
      return AlertTriangle;
    case "blocked":
      return Lock;
    case "pending":
    default:
      return Clock;
  }
}

function dotForStatus(status: GovernanceStatus): string {
  switch (status) {
    case "done":
      return "bg-[var(--pg-ok)]";
    case "running":
      return "bg-[var(--pg-blue)]";
    case "flagged":
      return "bg-[var(--pg-warn)]";
    case "blocked":
      return "bg-[var(--pg-danger)]";
    case "pending":
    default:
      return "bg-[var(--pg-border)]";
  }
}

function countFlagged(message: PlaygroundMessage | null): number {
  if (!message?.governance) return 0;
  return message.governance.steps.filter(
    (s) => s.status === "flagged" || s.status === "blocked"
  ).length;
}

/**
 * Tighten the always-visible PII meta when the backend ships many
 * types. The trace.py default emits ``"3 types · NIK, EMAIL, PHONE"``
 * which has to truncate inside the narrow stepper column. The full
 * list is already reachable via the expand-detail card, so we drop
 * the names from the headline once there are more than two — the
 * truncation stops being a load-bearing UX element, and operators
 * still see every type with one click.
 */
function compactMeta(step: GovernanceStep): string {
  if (step.id !== "pii" || !step.detail) return step.meta;
  const raw = (step.detail as { types?: unknown }).types;
  if (!Array.isArray(raw)) return step.meta;
  const types = raw.map((t) => String(t));
  if (types.length <= 2) return step.meta;
  return `${types.length} types`;
}


/**
 * Sesi PLAYGROUND-PROVIDER-TTFT-DISPLAY (v0.37.2) — render the labelled
 * first-token / streaming / total split for the provider step when
 * ttft_ms is present.
 *
 * The split values come from ``step.detail.ttft_ms`` + ``streaming_ms``
 * + ``stream_ms`` (total), which the playground pipeline populates from
 * the same ``ttft_ms_int`` it sends to the Observability aggregation.
 * Single source, two consumers — no drift.
 *
 * Falls back to no output when ttft is missing (empty completion or
 * upstream closed early) so the generic key:value loop renders the
 * remaining detail keys instead.
 */
function ProviderDetailExtras({ step }: { step: GovernanceStep }) {
  const { t } = useTranslation();
  if (step.id !== "provider" || !step.detail) return null;
  const detail = step.detail as Record<string, unknown>;
  const ttft = detail.ttft_ms;
  if (typeof ttft !== "number") return null;
  const streaming = typeof detail.streaming_ms === "number" ? detail.streaming_ms : null;
  const total = typeof detail.stream_ms === "number" ? detail.stream_ms : null;
  return (
    <div className="mb-2 space-y-0.5 border-b border-dashed pb-2" style={{ borderColor: "var(--pg-border)" }}>
      <ProviderDetailRow
        label={t("playground.governance.firstToken")}
        valueMs={ttft}
        emphasized
      />
      {streaming !== null && (
        <ProviderDetailRow
          label={t("playground.governance.streaming")}
          valueMs={streaming}
        />
      )}
      {total !== null && (
        <ProviderDetailRow
          label={t("playground.governance.total")}
          valueMs={total}
        />
      )}
    </div>
  );
}

function ProviderDetailRow({
  label,
  valueMs,
  emphasized,
}: {
  label: string;
  valueMs: number;
  emphasized?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span style={{ color: "var(--pg-text-3)" }}>{label}</span>
      <span
        className="mono"
        style={{
          color: emphasized ? "var(--pg-blue-dk)" : "var(--pg-text-2)",
          fontWeight: emphasized ? 500 : 400,
        }}
      >
        {valueMs}ms
      </span>
    </div>
  );
}

/**
 * Hide the raw provider-split keys from the generic key:value detail
 * loop when ``ProviderDetailExtras`` already renders them with labels.
 * Other detail keys (model, chunks_emitted) still surface so the
 * forensic view stays complete.
 */
function shouldHideDetailKey(step: GovernanceStep, key: string): boolean {
  if (step.id !== "provider" || !step.detail) return false;
  const detail = step.detail as Record<string, unknown>;
  if (typeof detail.ttft_ms !== "number") return false;
  return key === "ttft_ms" || key === "streaming_ms" || key === "stream_ms";
}
